/**
 * How much the device can draw. A tier is picked at start from what the browser says about the
 * device; then the adaptive controller watches real frame times while the view moves and steps the
 * resolution down first, then the splat budget, and back up when there is room.
 */

export interface DeviceInfo {
  ios: boolean;
  android: boolean;
  mobile: boolean;
  gpu: string;
  memoryGB?: number;
  cores: number;
  cssWidth: number;
  cssHeight: number;
  dpr: number;
}

export type TierName = 'laptop' | 'laptop-integrated' | 'phone' | 'phone-light' | 'phone-older';

export interface Tier {
  name: TierName;
  /** Frame time to hold while moving: 60 fps on laptops, 30 on phones. */
  budgetMs: number;
  /** Where the resolution starts, and how far it may climb or fall. */
  pixelRatioStart: number;
  pixelRatioMax: number;
  pixelRatioMin: number;
  /** Multiplies Spark's own per-platform splat count (2.5M desktop, 1.5M iOS, 1M Android): where it starts, its ceiling and floor. */
  splatScaleStart: number;
  splatScale: number;
  splatScaleMin: number;
  /** Splats the device keeps in memory at once (a multiple of Spark's 65,536 page). */
  maxPagedSplats: number;
  /** Full-precision splat positions (more memory, no rounding of far coordinates). */
  extSplats: boolean;
  /**
   * Shortest time between re-sorting the splats while the view moves. Each sort reads data back from
   * the graphics chip, which stalls a frame on integrated and phone graphics; sorting a little less
   * often while moving is invisible, the stalls are not.
   */
  sortIntervalMs: number;
  /**
   * The drawn floor's live reflection, as a share of the screen's resolution: it draws the splats a second time
   * from under the floor, so lighter phones draw none (the floor then mirrors only the room's average colour).
   */
  floorMirror: number;
  /** The floor's tile textures, pixels a side. */
  floorTile: number;
}

const PAGE = 65536;

const INTEGRATED = /intel|uhd|iris|hd graphics|radeon\(tm\) graphics|radeon graphics|vega \d|swiftshader|llvmpipe|microsoft basic/i;
const OLD_MOBILE_GPU = /adreno \(tm\) [345]\d\d\b|mali-t|mali-g(31|51|52|71|72)\b|powervr|videocore/i;
const MID_MOBILE_GPU = /adreno \(tm\) 6[0-4]\d\b|mali-g(57|68|76|77|78)\b/i;

/**
 * Each tier starts a little under what the device should manage and climbs when frames show room:
 * starting high and stuttering until it steps down feels worse than sharpening a moment later.
 */
export function detectTier(d: DeviceInfo): Tier {
  const shortSide = Math.min(d.cssWidth, d.cssHeight);
  const r = (cap: number) => Math.min(d.dpr, cap);
  if (!d.mobile) {
    // Measured on an Intel Iris Xe: 600,000 splats hold 60 fps while orbiting at full resolution, a million
    // don't, whatever the resolution, so integrated graphics start at 600,000 and probe upwards.
    return INTEGRATED.test(d.gpu)
      ? { name: 'laptop-integrated', budgetMs: 1000 / 60, pixelRatioStart: r(1), pixelRatioMax: r(1.5), pixelRatioMin: 0.6, splatScaleStart: 0.24, splatScale: 0.6, splatScaleMin: 0.12, maxPagedSplats: 128 * PAGE, extSplats: false, sortIntervalMs: 0, floorMirror: 0.33, floorTile: 768 }
      : { name: 'laptop', budgetMs: 1000 / 60, pixelRatioStart: r(1.5), pixelRatioMax: r(2), pixelRatioMin: 0.75, splatScaleStart: 0.6, splatScale: 1.2, splatScaleMin: 0.2, maxPagedSplats: 256 * PAGE, extSplats: true, sortIntervalMs: 0, floorMirror: 0.5, floorTile: 768 };
  }
  const mem = d.memoryGB;
  const older = (mem !== undefined && mem <= 2) || OLD_MOBILE_GPU.test(d.gpu) || (d.ios && shortSide <= 320);
  if (older) {
    return { name: 'phone-older', budgetMs: 1000 / 30, pixelRatioStart: r(1), pixelRatioMax: r(1), pixelRatioMin: 0.5, splatScaleStart: 0.25, splatScale: 0.4, splatScaleMin: 0.1, maxPagedSplats: 32 * PAGE, extSplats: false, sortIntervalMs: 0, floorMirror: 0, floorTile: 384 };
  }
  const light = (mem !== undefined && mem <= 4) || MID_MOBILE_GPU.test(d.gpu) || d.cores <= 4 || (d.ios && shortSide <= 375);
  if (light) {
    return { name: 'phone-light', budgetMs: 1000 / 30, pixelRatioStart: r(1.25), pixelRatioMax: r(1.5), pixelRatioMin: 0.6, splatScaleStart: 0.4, splatScale: 0.7, splatScaleMin: 0.15, maxPagedSplats: 64 * PAGE, extSplats: false, sortIntervalMs: 0, floorMirror: 0, floorTile: 512 };
  }
  return { name: 'phone', budgetMs: 1000 / 30, pixelRatioStart: r(1.5), pixelRatioMax: r(2), pixelRatioMin: 0.75, splatScaleStart: 0.6, splatScale: 1, splatScaleMin: 0.2, maxPagedSplats: (d.ios ? 96 : 128) * PAGE, extSplats: false, sortIntervalMs: 0, floorMirror: 0.3, floorTile: 512 };
}

/** A lighter tier after the graphics memory ran out (context lost) or the scan failed to fit. */
export function lighterTier(t: Tier): Tier {
  return {
    ...t,
    name: t.name.startsWith('laptop') ? 'laptop-integrated' : 'phone-older',
    pixelRatioStart: Math.max(0.5, t.pixelRatioStart * 0.75),
    pixelRatioMax: Math.max(0.5, t.pixelRatioMax * 0.75),
    pixelRatioMin: Math.max(0.5, t.pixelRatioMin * 0.8),
    splatScaleStart: Math.max(0.15, t.splatScaleStart * 0.6),
    splatScale: Math.max(0.2, t.splatScale * 0.6),
    splatScaleMin: Math.max(0.1, t.splatScaleMin * 0.7),
    maxPagedSplats: Math.max(16 * PAGE, Math.floor((t.maxPagedSplats * 0.5) / PAGE) * PAGE),
    extSplats: false,
    floorMirror: t.name.startsWith('laptop') ? Math.round(t.floorMirror * 0.6 * 100) / 100 : 0,
    floorTile: Math.min(t.floorTile, 512),
  };
}

export function readDevice(gl?: WebGL2RenderingContext | null): DeviceInfo {
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  const android = /Android/.test(ua);
  let gpu = '';
  if (gl) {
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    gpu = String(ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER));
  }
  const nav = navigator as Navigator & { deviceMemory?: number };
  return {
    ios,
    android,
    mobile: ios || android || /Mobi/.test(ua),
    gpu,
    memoryGB: nav.deviceMemory,
    cores: navigator.hardwareConcurrency || 4,
    cssWidth: innerWidth,
    cssHeight: innerHeight,
    dpr: devicePixelRatio || 1,
  };
}

export interface QualitySettings {
  pixelRatio: number;
  splatScale: number;
}

/**
 * Watches frame times while the view moves. Twice a second it looks at the slowest tenth of recent
 * frames, so the occasional hitch counts, not just the typical frame. Too slow trims the splat budget
 * first (on integrated and phone graphics the splat count costs more than the resolution), then the
 * resolution. A screen's refresh caps frame times, so a device with room to spare looks the same as
 * one just keeping up: after a few seconds of steady frames it tries one step up, and takes it back
 * (and stops trying that high) if the hitches return.
 */
export class AdaptiveQuality {
  pixelRatio: number;
  splatScale: number;
  stepDowns = 0;
  private samples: number[] = [];
  private lastCheck = 0;
  private lastChange = -Infinity;
  private steadyChecks = 0;
  private probe: { at: number; before: QualitySettings } | null = null;
  /** Just undid a step up: the hitches take a moment to clear, so wait longer before trimming more. */
  private reverted = false;
  private ceiling: QualitySettings;

  constructor(private tier: Tier, start?: Partial<QualitySettings>) {
    this.pixelRatio = start?.pixelRatio ?? tier.pixelRatioStart;
    this.splatScale = start?.splatScale ?? tier.splatScaleStart;
    this.ceiling = { pixelRatio: tier.pixelRatioMax, splatScale: tier.splatScale };
  }

  get settings(): QualitySettings {
    return { pixelRatio: this.pixelRatio, splatScale: this.splatScale };
  }

  /** For a view standing still: the tier's sharpest, since there is no motion to stutter. */
  get still(): QualitySettings {
    return { pixelRatio: this.tier.pixelRatioMax, splatScale: this.tier.splatScale };
  }

  /** Frames while nothing moves (or right after a pause) say nothing about smoothness; drop them. */
  reset() {
    this.samples.length = 0;
    this.steadyChecks = 0;
  }

  private stepDown(): boolean {
    const t = this.tier;
    // splats first, down to well under where the tier started; then the resolution; then splats again
    if (this.splatScale > Math.max(t.splatScaleMin, t.splatScaleStart * 0.6) + 1e-3) {
      this.splatScale = Math.max(t.splatScaleMin, round2(this.splatScale * 0.8));
    } else if (this.pixelRatio > t.pixelRatioMin + 1e-3) {
      this.pixelRatio = Math.max(t.pixelRatioMin, round2(this.pixelRatio * 0.85));
    } else if (this.splatScale > t.splatScaleMin + 1e-3) {
      this.splatScale = Math.max(t.splatScaleMin, round2(this.splatScale * 0.8));
    } else {
      return false;
    }
    return true;
  }

  private stepUp(): boolean {
    // the resolution comes back first (it was the last to go), then the splats
    if (this.pixelRatio < Math.min(this.ceiling.pixelRatio, this.tier.pixelRatioStart) - 1e-3) {
      this.pixelRatio = Math.min(this.ceiling.pixelRatio, round2(this.pixelRatio * 1.1));
    } else if (this.splatScale < this.ceiling.splatScale - 1e-3) {
      this.splatScale = Math.min(this.ceiling.splatScale, round2(this.splatScale * 1.15));
    } else if (this.pixelRatio < this.ceiling.pixelRatio - 1e-3) {
      this.pixelRatio = Math.min(this.ceiling.pixelRatio, round2(this.pixelRatio * 1.1));
    } else {
      return false;
    }
    return true;
  }

  /** One frame's time, taken while the view moves. Returns true when the settings changed. */
  sample(ms: number, now: number): boolean {
    if (!(ms > 0) || ms > 1000) return false;
    this.samples.push(ms);
    if (this.samples.length > 60) this.samples.shift();
    if (now - this.lastCheck < 500 || this.samples.length < 12) return false;
    this.lastCheck = now;
    const sorted = [...this.samples].sort((a, b) => a - b);
    const at = (q: number) => sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * q))]!;
    const t = this.tier;
    if (at(0.9) > t.budgetMs * 1.15 && now - this.lastChange > (this.reverted ? 1500 : 600)) {
      this.reverted = false;
      this.steadyChecks = 0;
      if (this.probe && now - this.probe.at < 3000) {
        // the last step up brought hitches back: undo it, and don't try that high again
        const before = this.probe.before;
        if (this.splatScale > before.splatScale) this.ceiling.splatScale = before.splatScale;
        if (this.pixelRatio > before.pixelRatio) this.ceiling.pixelRatio = before.pixelRatio;
        this.pixelRatio = before.pixelRatio;
        this.splatScale = before.splatScale;
        this.probe = null;
        this.reverted = true;
      } else {
        // far too slow: take two steps at once rather than stutter through them one by one
        const steps = at(0.5) > t.budgetMs * 1.5 ? 2 : 1;
        let changed = false;
        for (let i = 0; i < steps; i++) changed = this.stepDown() || changed;
        if (!changed) return false;
        this.probe = null;
      }
      this.stepDowns++;
      this.lastChange = now;
      this.samples.length = 0;
      return true;
    }
    if (at(0.95) <= t.budgetMs * 1.08) {
      this.steadyChecks++;
      if (this.steadyChecks >= 6 && now - this.lastChange > 4000) {
        const before = this.settings;
        this.steadyChecks = 0;
        if (!this.stepUp()) return false;
        this.probe = { at: now, before };
        this.lastChange = now;
        this.samples.length = 0;
        return true;
      }
    } else {
      this.steadyChecks = 0;
    }
    return false;
  }
}

function round2(v: number) {
  return Math.round(v * 100) / 100;
}
